<script setup lang="ts">
import { computed } from 'vue';
import type { ModifierGroup, MenuItemModifierGroupInput } from '@/types/menu';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();

const props = defineProps<{
  modelValue: MenuItemModifierGroupInput[];
  groups: ModifierGroup[];
}>();

const emit = defineEmits<{
  (e: 'update:modelValue', value: MenuItemModifierGroupInput[]): void;
}>();

const selectedLinks = computed(() => props.modelValue || []);

const isSelected = (groupId: number) => selectedLinks.value.some(link => link.group_id === groupId);

const getLink = (groupId: number): MenuItemModifierGroupInput | undefined => {
  return selectedLinks.value.find(link => link.group_id === groupId);
};

const toggleGroup = (group: ModifierGroup) => {
  if (isSelected(group.id)) {
    emit('update:modelValue', selectedLinks.value.filter(link => link.group_id !== group.id));
    return;
  }

  const newLink: MenuItemModifierGroupInput = {
    group_id: group.id,
    required: group.required_default ?? false,
    min_select: group.min_select_default ?? 0,
    max_select: group.max_select_default ?? 1,
    sort_order: group.sort_order ?? 0,
  };

  emit('update:modelValue', [...selectedLinks.value, newLink]);
};

const updateLink = (groupId: number, patch: Partial<MenuItemModifierGroupInput>) => {
  const updated = selectedLinks.value.map(link => {
    if (link.group_id !== groupId) return link;

    const next = { ...link, ...patch };
    const min = next.min_select ?? 0;
    const max = next.max_select ?? 1;

    if (max < min) {
      next.max_select = min;
    }

    return next;
  });

  emit('update:modelValue', updated);
};
</script>

<template>
  <div class="space-y-4">
    <div>
      <h3 class="text-sm font-medium text-gray-700 dark:text-gray-100">
        {{ t('app.errors.menu.modifier_groups.title') }}
      </h3>
      <p class="text-xs text-gray-500 dark:text-gray-400">
        {{ t('app.errors.menu.modifier_groups.description') }}
      </p>
    </div>

    <div
      v-if="groups.length === 0"
      class="text-sm text-gray-500 dark:text-gray-400"
    >
      {{ t('app.errors.menu.modifier_groups.no_groups') }}
    </div>

    <div
      v-for="group in groups"
      :key="group.id"
      class="rounded-lg border border-gray-200 dark:border-gray-700 p-3"
    >
      <div class="flex items-start justify-between gap-3">
        <div>
          <p class="text-sm font-medium text-gray-900 dark:text-gray-100">
            {{ group.name }}
          </p>
          <p
            v-if="group.description"
            class="text-xs text-gray-500 dark:text-gray-400"
          >
            {{ group.description }}
          </p>
        </div>
        <label class="inline-flex items-center gap-2 text-sm text-gray-700 dark:text-gray-200">
          <input
            type="checkbox"
            class="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
            :checked="isSelected(group.id)"
            @change="toggleGroup(group)"
          >
          {{ t('app.errors.menu.modifier_groups.use') }}
        </label>
      </div>

      <div
        v-if="isSelected(group.id)"
        class="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-3"
      >
        <div>
          <label class="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">{{ t('app.errors.menu.modifier_groups.min_select') }}</label>
          <input
            type="number"
            min="0"
            class="w-full rounded-md border border-gray-300 dark:border-gray-700 dark:bg-gray-900 dark:text-white px-2 py-1 text-sm"
            :value="getLink(group.id)?.min_select ?? 0"
            @input="updateLink(group.id, { min_select: Number(($event.target as HTMLInputElement).value) })"
          >
        </div>
        <div>
          <label class="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">{{ t('app.errors.menu.modifier_groups.max_select') }}</label>
          <input
            type="number"
            min="1"
            class="w-full rounded-md border border-gray-300 dark:border-gray-700 dark:bg-gray-900 dark:text-white px-2 py-1 text-sm"
            :value="getLink(group.id)?.max_select ?? 1"
            @input="updateLink(group.id, { max_select: Number(($event.target as HTMLInputElement).value) })"
          >
        </div>
        <div class="flex items-end">
          <label class="inline-flex items-center gap-2 text-sm text-gray-700 dark:text-gray-200">
            <input
              type="checkbox"
              class="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
              :checked="getLink(group.id)?.required ?? false"
              @change="updateLink(group.id, { required: ($event.target as HTMLInputElement).checked })"
            >
            {{ t('app.errors.menu.modifier_groups.required') }}
          </label>
        </div>
      </div>
    </div>
  </div>
</template>
